import React, { useState, useEffect, useMemo } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/Form/Input'
import { Checkbox } from '../../components/Form/Checkbox'
import { FieldHint } from '../../components/Form/FieldHint'
import { Select } from '../../components/Form/Select'
import { Textarea } from '../../components/Form/Textarea'
import { EntityEditLayout, ErrorBanner, LoadingSpinner, NotFound } from '../../components'
import { authService } from '../../services/auth.service'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useBulkWine, useSaveBulkWine } from '../../hooks/useBulkWines'
import { bulkWinesService } from '../../services/bulk-wines.service'
import { useUser } from '../../hooks/useUsers'
import { useCountries, useRegions, useSubRegions, useVarieties } from '../../hooks/useReferenceData'
import { useQueryClient } from '@tanstack/react-query'
import { USER_ROLES } from '@ministry-of-wine/shared'

interface Supplier {
	id: number
	name: string
}

interface Region {
	id: number
	name: string
	countryId: number
}

interface SubRegion {
	id: number
	name: string
	regionId: number
}

interface Country {
	id: number
	name: string
}

interface Variety {
	id: number
	name: string
}

interface BulkWineFormData {
	code: string
	supplierId: string
	countryId: string
	regionId: string
	subRegionId: string
	// Where the wine is stored, for buyer freight estimates. Not tied to countryId —
	// stored wine can sit in a different region or country from its origin.
	locationRegionId: string
	locationSubRegionId: string
	primaryVarietyId: string
	vintage: string
	priceCurrency: string
	pricePerLiter: string
	priceMargin: string
	priceMarginUnit: '%' | '$'
	totalVolumeLiters: string
	compositions: Array<{
		varietyId: string
		percentage: string
	}>
	// Technical Specifications
	alcoholPercentage: string
	phLevel: string
	volatileAcidity: string
	totalAcidity: string
	residualSugar: string
	freeSo2: string
	totalSo2: string
	technicalAnalysisDate: string
	// Compliance
	suitableVegetarians: boolean
	suitableVegans: boolean
	exportCertified: boolean
	sprayProgrammeFollowed: boolean
	oakTreatment: boolean
	proteinStable: boolean
	wineEnriched: boolean
	calibratedFlowMeter: boolean
	filtered: boolean
	wineSweetened: boolean
	coldStable: boolean
	mlfComplete: boolean
	oakType: string
	oakTreatmentNote: string
	filterType: string
	filterNote: string
	// Export Eligibility
	exportToEu: boolean
	exportToUk: boolean
	exportToChina: boolean
	exportToJapan: boolean
	exportToCanada: boolean
	exportToUsa: boolean
	// NZ Eligibility
	winerySwinzCertified: boolean
	vineyardSwinzCertified: boolean
	nzComplianceStatement: boolean
	// AU Eligibility
	auComplianceStatement: boolean
}

export const BulkWineEditPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [searchParams] = useSearchParams()
	const isEditMode = Boolean(id)

	const suppliersParams = new URLSearchParams({ page: '1', limit: '1000' })
	const currentUser = authService.getStoredUser()
	const isSupplierAdmin = currentUser?.role === 'supplier_admin'
	// Margins are superadmin-only; suppliers can see/edit price per litre but not the margin.
	const isSuperadmin = currentUser?.role === USER_ROLES.SUPERADMIN
	const { data: currentUserDetails } = useUser(currentUser?.id ?? '', Boolean(currentUser?.id) && isSupplierAdmin)
	const { data: suppliersResponse } = useSuppliers(suppliersParams)
	const { data: regionsData } = useRegions()
	const { data: subRegionsData } = useSubRegions()
	const { data: countriesData } = useCountries()
	const { data: varietiesData } = useVarieties()

	// Use renderState for proper error handling
	const { data: bulkWineData, renderState } = useBulkWine(id as string, isEditMode && Boolean(id))

	const saveBulkWineMutation = useSaveBulkWine()
	const [mutationError, setMutationError] = useState<Error | null>(null)

	const suppliers = useMemo(() => (suppliersResponse?.data || []) as Supplier[], [suppliersResponse?.data])
	const regions = useMemo(() => (regionsData || []) as Region[], [regionsData])
	const subRegions = useMemo(() => (subRegionsData || []) as SubRegion[], [subRegionsData])
	const countries = useMemo(() => (countriesData || []) as Country[], [countriesData])
	const varieties = useMemo(() => (varietiesData || []) as Variety[], [varietiesData])
	const [filteredRegions, setFilteredRegions] = useState<Region[]>([])
	const [selectedCountryId, setSelectedCountryId] = useState('')
	const supplierAdminSupplierId =
		currentUser?.supplierId !== null && currentUser?.supplierId !== undefined
			? String(currentUser.supplierId)
			: currentUserDetails?.supplierId !== null && currentUserDetails?.supplierId !== undefined
				? String(currentUserDetails.supplierId)
				: ''
	const supplierAdminSupplierName =
		currentUser?.supplierName ||
		currentUserDetails?.supplierName ||
		suppliers.find((supplier) => String(supplier.id) === supplierAdminSupplierId)?.name ||
		''
	const preselectedSupplierId = searchParams.get('supplierId') || ''

	// TypeScript: loadedBulkWine will be defined after renderState checks
	const loadedBulkWine = isEditMode && renderState.status === 'success' ? renderState.data : null

	// ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
	const [formData, setFormData] = useState<BulkWineFormData>({
		code: '',
		supplierId: '',
		countryId: '',
		regionId: '',
		subRegionId: '',
		locationRegionId: '',
		locationSubRegionId: '',
		primaryVarietyId: '',
		vintage: '2024',
		priceCurrency: 'NZD',
		pricePerLiter: '',
		priceMargin: '',
		priceMarginUnit: '%',
		totalVolumeLiters: '',
		compositions: [{ varietyId: '', percentage: '100' }],
		// Technical Specifications
		alcoholPercentage: '',
		phLevel: '',
		volatileAcidity: '',
		totalAcidity: '',
		residualSugar: '',
		freeSo2: '',
		totalSo2: '',
		technicalAnalysisDate: '',
		// Compliance
		suitableVegetarians: false,
		suitableVegans: false,
		exportCertified: false,
		sprayProgrammeFollowed: false,
		oakTreatment: false,
		proteinStable: false,
		wineEnriched: false,
		calibratedFlowMeter: false,
		filtered: false,
		wineSweetened: false,
		coldStable: false,
		mlfComplete: false,
		oakType: '',
		oakTreatmentNote: '',
		filterType: '',
		filterNote: '',
		// Export Eligibility
		exportToEu: false,
		exportToUk: false,
		exportToChina: false,
		exportToJapan: false,
		exportToCanada: false,
		exportToUsa: false,
		// NZ Eligibility
		winerySwinzCertified: false,
		vineyardSwinzCertified: false,
		nzComplianceStatement: false,
		// AU Eligibility
		auComplianceStatement: false,
	})

	const [errors, setErrors] = useState<Partial<Record<string, string>>>({})

	// The wine code auto-fills from vintage + variety on new specs until the user
	// edits it manually. Existing records load with a code already set, so auto
	// mode starts off in edit mode.
	const [codeAuto, setCodeAuto] = useState(!isEditMode)
	const [regeneratingCode, setRegeneratingCode] = useState(false)

	// The Region dropdown lists each region with its sub-regions nested underneath.
	// A retired (soft-deleted) sub-region still stored on the record is kept as an option
	// so editing the wine does not silently drop it.
	const regionOptionGroups = useMemo(() => {
		const storedSubRegionId = formData.subRegionId
		const storedIsKnown = subRegions.some((sr) => String(sr.id) === storedSubRegionId)

		return filteredRegions.map((region) => {
			const matching = subRegions.filter((sr) => String(sr.regionId) === String(region.id))

			if (
				storedSubRegionId &&
				!storedIsKnown &&
				String(region.id) === formData.regionId &&
				loadedBulkWine?.subRegionName
			) {
				matching.push({
					id: Number(storedSubRegionId),
					name: `${loadedBulkWine.subRegionName} (retired)`,
					regionId: Number(region.id),
				})
			}

			return { region, subRegions: matching }
		})
	}, [filteredRegions, subRegions, formData.regionId, formData.subRegionId, loadedBulkWine])

	// Sub-region selections encode as `s:<id>`, plain regions as `r:<id>`.
	const regionSelectValue = formData.subRegionId
		? `s:${formData.subRegionId}`
		: formData.regionId
			? `r:${formData.regionId}`
			: ''

	const handleRegionSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value

		setFormData((prev) => {
			if (!value) {
				return { ...prev, regionId: '', subRegionId: '' }
			}

			const [kind, rawId] = value.split(':')
			if (kind === 's') {
				const subRegion = subRegions.find((sr) => String(sr.id) === rawId)
				return {
					...prev,
					// A retired sub-region is not in the reference list; it can only have been
					// rendered under the region already on the record, so that region still applies.
					regionId: subRegion ? String(subRegion.regionId) : prev.regionId,
					subRegionId: rawId,
				}
			}

			return { ...prev, regionId: rawId, subRegionId: '' }
		})

		if (errors.regionId) {
			setErrors((prev) => ({ ...prev, regionId: undefined }))
		}
	}

	// The Location dropdown offers the same region/sub-region options as Region, but over
	// the *full* catalogue rather than the selected country's regions: the wine's storage
	// location is independent of where its fruit was grown, so restricting it to the
	// origin country would make the field unusable for wine held offshore.
	// A retired (soft-deleted) sub-region already on the record is kept as an option so
	// editing the wine does not silently drop it.
	const locationOptionGroups = useMemo(() => {
		const storedSubRegionId = formData.locationSubRegionId
		const storedIsKnown = subRegions.some((sr) => String(sr.id) === storedSubRegionId)

		return regions.map((region) => {
			const matching = subRegions.filter((sr) => String(sr.regionId) === String(region.id))

			if (
				storedSubRegionId &&
				!storedIsKnown &&
				String(region.id) === formData.locationRegionId &&
				loadedBulkWine?.locationSubRegionName
			) {
				matching.push({
					id: Number(storedSubRegionId),
					name: `${loadedBulkWine.locationSubRegionName} (retired)`,
					regionId: Number(region.id),
				})
			}

			return { region, subRegions: matching }
		})
	}, [regions, subRegions, formData.locationRegionId, formData.locationSubRegionId, loadedBulkWine])

	// Mirrors the Region encoding: sub-regions as `s:<id>`, plain regions as `r:<id>`.
	const locationSelectValue = formData.locationSubRegionId
		? `s:${formData.locationSubRegionId}`
		: formData.locationRegionId
			? `r:${formData.locationRegionId}`
			: ''

	const handleLocationSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value

		setFormData((prev) => {
			if (!value) {
				return { ...prev, locationRegionId: '', locationSubRegionId: '' }
			}

			const [kind, rawId] = value.split(':')
			if (kind === 's') {
				const subRegion = subRegions.find((sr) => String(sr.id) === rawId)
				return {
					...prev,
					// A retired sub-region is not in the reference list; it can only have been
					// rendered under the region already on the record, so that region still applies.
					locationRegionId: subRegion ? String(subRegion.regionId) : prev.locationRegionId,
					locationSubRegionId: rawId,
				}
			}

			return { ...prev, locationRegionId: rawId, locationSubRegionId: '' }
		})
	}

	const canGenerateCode = /^(19|20)\d{2}$/.test(formData.vintage.trim()) && Boolean(formData.primaryVarietyId)

	useEffect(() => {
		if (isEditMode && loadedBulkWine && regions.length > 0) {
			const region = regions.find((r) => r.id === loadedBulkWine.regionId)
			if (region) {
				setSelectedCountryId(String(region.countryId))
			}

			const normalizedCompositions =
				Array.isArray(loadedBulkWine.compositions) && loadedBulkWine.compositions.length > 0
					? loadedBulkWine.compositions.map((comp: any) => ({
							varietyId: comp.varietyId ? String(comp.varietyId) : '',
							percentage: comp.percentage ? String(comp.percentage) : '',
						}))
					: [
							{
								varietyId: loadedBulkWine.primaryVarietyId ? String(loadedBulkWine.primaryVarietyId) : '',
								percentage: '100',
							},
						]

			setFormData({
				code: loadedBulkWine.name || '',
				supplierId:
					loadedBulkWine.supplierId !== null && loadedBulkWine.supplierId !== undefined
						? String(bulkWineData.supplierId)
						: '',
				countryId: region ? String(region.countryId) : '',
				regionId:
					bulkWineData.regionId !== null && bulkWineData.regionId !== undefined ? String(bulkWineData.regionId) : '',
				subRegionId:
					bulkWineData.subRegionId !== null && bulkWineData.subRegionId !== undefined
						? String(bulkWineData.subRegionId)
						: '',
				locationRegionId:
					bulkWineData.locationRegionId !== null && bulkWineData.locationRegionId !== undefined
						? String(bulkWineData.locationRegionId)
						: '',
				locationSubRegionId:
					bulkWineData.locationSubRegionId !== null && bulkWineData.locationSubRegionId !== undefined
						? String(bulkWineData.locationSubRegionId)
						: '',
				primaryVarietyId:
					bulkWineData.primaryVarietyId !== null && bulkWineData.primaryVarietyId !== undefined
						? String(bulkWineData.primaryVarietyId)
						: '',
				vintage:
					bulkWineData.vintage !== null && bulkWineData.vintage !== undefined ? String(bulkWineData.vintage) : '',
				priceCurrency: bulkWineData.priceCurrency || 'NZD',
				pricePerLiter:
					bulkWineData.pricePerLiter !== null && bulkWineData.pricePerLiter !== undefined
						? String(bulkWineData.pricePerLiter)
						: '',
				priceMargin:
					bulkWineData.priceMargin !== null && bulkWineData.priceMargin !== undefined
						? String(bulkWineData.priceMargin)
						: '',
				priceMarginUnit: bulkWineData.priceMarginUnit === '$' ? '$' : '%',
				totalVolumeLiters:
					bulkWineData.totalVolumeLiters !== null && bulkWineData.totalVolumeLiters !== undefined
						? String(bulkWineData.totalVolumeLiters)
						: '',
				compositions: normalizedCompositions,
				// Technical Specifications
				alcoholPercentage:
					bulkWineData.alcoholPercentage !== null && bulkWineData.alcoholPercentage !== undefined
						? String(bulkWineData.alcoholPercentage)
						: '',
				phLevel:
					bulkWineData.phLevel !== null && bulkWineData.phLevel !== undefined ? String(bulkWineData.phLevel) : '',
				volatileAcidity:
					bulkWineData.volatileAcidity !== null && bulkWineData.volatileAcidity !== undefined
						? String(bulkWineData.volatileAcidity)
						: '',
				totalAcidity:
					bulkWineData.totalAcidity !== null && bulkWineData.totalAcidity !== undefined
						? String(bulkWineData.totalAcidity)
						: '',
				residualSugar:
					bulkWineData.residualSugar !== null && bulkWineData.residualSugar !== undefined
						? String(bulkWineData.residualSugar)
						: '',
				freeSo2:
					bulkWineData.freeSo2 !== null && bulkWineData.freeSo2 !== undefined ? String(bulkWineData.freeSo2) : '',
				totalSo2:
					bulkWineData.totalSo2 !== null && bulkWineData.totalSo2 !== undefined ? String(bulkWineData.totalSo2) : '',
				technicalAnalysisDate: bulkWineData.technicalAnalysisDate
					? String(bulkWineData.technicalAnalysisDate).slice(0, 10)
					: '',
				// Compliance
				suitableVegetarians: bulkWineData.suitableVegetarians ?? false,
				suitableVegans: bulkWineData.suitableVegans ?? false,
				exportCertified: bulkWineData.exportCertified ?? false,
				sprayProgrammeFollowed: bulkWineData.sprayProgrammeFollowed ?? false,
				oakTreatment: bulkWineData.oakTreatment ?? false,
				proteinStable: bulkWineData.proteinStable ?? false,
				wineEnriched: bulkWineData.wineEnriched ?? false,
				calibratedFlowMeter: bulkWineData.calibratedFlowMeter ?? false,
				filtered: bulkWineData.filtered ?? false,
				wineSweetened: bulkWineData.wineSweetened ?? false,
				coldStable: bulkWineData.coldStable ?? false,
				mlfComplete: bulkWineData.mlfComplete ?? false,
				oakType: bulkWineData.oakType || '',
				oakTreatmentNote: bulkWineData.oakTreatmentNote || '',
				filterType: bulkWineData.filterType || '',
				filterNote: bulkWineData.filterNote || '',
				// Export Eligibility
				exportToEu: bulkWineData.exportToEu ?? false,
				exportToUk: bulkWineData.exportToUk ?? false,
				exportToChina: bulkWineData.exportToChina ?? false,
				exportToJapan: bulkWineData.exportToJapan ?? false,
				exportToCanada: bulkWineData.exportToCanada ?? false,
				exportToUsa: bulkWineData.exportToUsa ?? false,
				// NZ Eligibility
				winerySwinzCertified: bulkWineData.winerySwinzCertified ?? false,
				vineyardSwinzCertified: bulkWineData.vineyardSwinzCertified ?? false,
				nzComplianceStatement: bulkWineData.nzComplianceStatement ?? false,
				// AU Eligibility
				auComplianceStatement: bulkWineData.auComplianceStatement ?? false,
			})
		}
	}, [loadedBulkWine, isEditMode, regions]) // ✅ FIX: Use loadedBulkWine instead of bulkWineData to prevent infinite loop

	useEffect(() => {
		if (!selectedCountryId) {
			setFilteredRegions(regions)
			return
		}

		const filtered = regions.filter((r) => String(r.countryId) === selectedCountryId)
		setFilteredRegions(filtered)
	}, [selectedCountryId, regions]) // ✅ FIX: Just filter regions, don't touch formData here

	useEffect(() => {
		if (isEditMode || isSupplierAdmin || !preselectedSupplierId) {
			return
		}

		setFormData((prev) =>
			prev.supplierId === preselectedSupplierId ? prev : { ...prev, supplierId: preselectedSupplierId },
		)

		if (errors.supplierId) {
			setErrors((prev) => ({ ...prev, supplierId: undefined }))
		}
	}, [isEditMode, isSupplierAdmin, preselectedSupplierId]) // ✅ FIX: Removed errors.supplierId from dependencies

	useEffect(() => {
		if (!isSupplierAdmin || !supplierAdminSupplierId) {
			return
		}

		setFormData((prev) =>
			prev.supplierId === supplierAdminSupplierId ? prev : { ...prev, supplierId: supplierAdminSupplierId },
		)

		if (errors.supplierId) {
			setErrors((prev) => ({ ...prev, supplierId: undefined }))
		}
	}, [isSupplierAdmin, supplierAdminSupplierId]) // ✅ FIX: Removed errors.supplierId from dependencies

	// Auto-fill the wine code from vintage + variety while in auto mode. Debounced
	// so typing a vintage doesn't fire a request per keystroke. Failures are
	// non-blocking — the user can always type a code by hand.
	useEffect(() => {
		if (!codeAuto || !canGenerateCode) {
			return
		}

		let cancelled = false
		const handle = setTimeout(async () => {
			try {
				const { code } = await bulkWinesService.generateCode(
					parseInt(formData.vintage, 10),
					parseInt(formData.primaryVarietyId, 10),
				)
				if (!cancelled) {
					setFormData((prev) => ({ ...prev, code }))
					setErrors((prev) => (prev.code ? { ...prev, code: undefined } : prev))
				}
			} catch {
				// Non-critical helper; leave the field as-is on failure.
			}
		}, 400)

		return () => {
			cancelled = true
			clearTimeout(handle)
		}
	}, [codeAuto, canGenerateCode, formData.vintage, formData.primaryVarietyId])

	const handleRegenerateCode = async () => {
		if (!canGenerateCode) {
			return
		}

		setRegeneratingCode(true)
		try {
			const { code } = await bulkWinesService.generateCode(
				parseInt(formData.vintage, 10),
				parseInt(formData.primaryVarietyId, 10),
			)
			setCodeAuto(true)
			setFormData((prev) => ({ ...prev, code }))
			setErrors((prev) => (prev.code ? { ...prev, code: undefined } : prev))
		} catch (error) {
			setMutationError(error as Error)
		} finally {
			setRegeneratingCode(false)
		}
	}

	// ALL HOOKS CALLED ABOVE - NOW SAFE TO DO CONDITIONAL RETURNS

	// Handle loading state (only in edit mode)
	if (isEditMode && renderState.status === 'loading') {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<LoadingSpinner message="Loading bulk wine data..." />
			</div>
		)
	}

	// Handle error state (only in edit mode)
	if (isEditMode && renderState.status === 'error') {
		return (
			<div className="max-w-2xl mx-auto mt-8 p-4">
				<ErrorBanner
					error={renderState.error}
					title="Error Loading Bulk Wine"
					onRetry={() => {
						queryClient.invalidateQueries({ queryKey: ['bulk-wines', id] })
					}}
				/>
			</div>
		)
	}

	// Handle empty state (wine not found) - only in edit mode
	if (isEditMode && renderState.status === 'empty') {
		return (
			<NotFound
				message={`Bulk wine with ID ${id} not found`}
				action={{
					label: 'Back to Bulk Wines',
					onClick: () => navigate('/inventory/bulk-wine'),
				}}
			/>
		)
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		const { name, value, type } = e.target
		const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}))

		// Editing the code by hand opts out of auto-generation.
		if (name === 'code') {
			setCodeAuto(false)
		}

		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }))
		}
	}

	const handleCheckboxChange = (name: keyof BulkWineFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [name]: e.target.checked }))
	}

	const handleSelectChange = (name: keyof BulkWineFormData) => (value: string) => {
		setFormData((prev) => ({ ...prev, [name]: value }))
	}

	const getRemainingCompositionPercentage = (compositions: BulkWineFormData['compositions']): string => {
		const usedPercentage = compositions.reduce((sum, comp) => sum + (parseFloat(comp.percentage) || 0), 0)
		const remainingPercentage = Math.max(0, 100 - usedPercentage)

		return Number.isInteger(remainingPercentage)
			? String(remainingPercentage)
			: remainingPercentage.toFixed(2).replace(/\.?0+$/, '')
	}

	const getSelectedCompositionVarietyIds = (excludeIndex?: number): string[] =>
		formData.compositions
			.filter((_, index) => index !== excludeIndex)
			.map((comp) => comp.varietyId)
			.filter(Boolean)

	const getAvailableCompositionVarieties = (currentIndex: number): Variety[] => {
		const selectedVarietyIds = new Set(getSelectedCompositionVarietyIds(currentIndex))
		return varieties.filter((variety) => !selectedVarietyIds.has(String(variety.id)))
	}

	const getAvailableVarietiesForNewComposition = (): Variety[] => {
		const selectedVarietyIds = new Set(getSelectedCompositionVarietyIds())
		return varieties.filter((variety) => !selectedVarietyIds.has(String(variety.id)))
	}

	const canAddComposition = (): boolean =>
		getAvailableVarietiesForNewComposition().length > 0 &&
		parseFloat(getRemainingCompositionPercentage(formData.compositions)) > 0

	const handleCompositionChange = (index: number, field: 'varietyId' | 'percentage', value: string) => {
		setFormData((prev) => {
			const newCompositions = [...prev.compositions]
			newCompositions[index] = { ...newCompositions[index], [field]: value }
			return { ...prev, compositions: newCompositions }
		})
	}

	const addComposition = () => {
		if (!canAddComposition()) {
			return
		}

		setFormData((prev) => ({
			...prev,
			compositions: [
				...prev.compositions,
				{ varietyId: '', percentage: getRemainingCompositionPercentage(prev.compositions) },
			],
		}))
	}

	const removeComposition = (index: number) => {
		if (formData.compositions.length > 1) {
			setFormData((prev) => ({
				...prev,
				compositions: prev.compositions.filter((_, i) => i !== index),
			}))
		}
	}

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {}

		if (!formData.code.trim()) {
			newErrors.code = 'Code is required'
		}

		if (!formData.supplierId) {
			newErrors.supplierId = 'Supplier is required'
		}

		if (!formData.regionId) {
			newErrors.regionId = 'Region is required'
		}
		if (!formData.countryId) {
			newErrors.countryId = 'Country is required'
		}
		if (!formData.primaryVarietyId) {
			newErrors.primaryVarietyId = 'Variety is required'
		}
		if (!formData.vintage.trim()) {
			newErrors.vintage = 'Vintage is required'
		} else if (!/^(19|20)\d{2}$/.test(formData.vintage.trim())) {
			newErrors.vintage = 'Vintage must be a valid 4-digit year'
		}

		if (!formData.pricePerLiter) {
			newErrors.pricePerLiter = 'Price per liter is required'
		} else if (isNaN(parseFloat(formData.pricePerLiter)) || parseFloat(formData.pricePerLiter) <= 0) {
			newErrors.pricePerLiter = 'Price must be a positive number'
		}

		if (formData.priceMargin) {
			if (isNaN(parseFloat(formData.priceMargin)) || parseFloat(formData.priceMargin) < 0) {
				newErrors.priceMargin = 'Margin must be zero or a positive number'
			}
		}

		if (
			formData.totalVolumeLiters &&
			(isNaN(parseFloat(formData.totalVolumeLiters)) || parseFloat(formData.totalVolumeLiters) <= 0)
		) {
			newErrors.totalVolumeLiters = 'Total volume must be a positive number'
		}

		// Validate Technical Specifications
		if (formData.alcoholPercentage) {
			const alcohol = parseFloat(formData.alcoholPercentage)
			if (isNaN(alcohol) || alcohol < 0 || alcohol > 100) {
				newErrors.alcoholPercentage = 'Alcohol must be between 0 and 100%'
			}
		}

		if (formData.phLevel) {
			const ph = parseFloat(formData.phLevel)
			if (isNaN(ph) || ph < 0 || ph > 14) {
				newErrors.phLevel = 'pH must be between 0 and 14'
			}
		}

		if (formData.volatileAcidity) {
			const va = parseFloat(formData.volatileAcidity)
			if (isNaN(va) || va < 0) {
				newErrors.volatileAcidity = 'VA must be a non-negative number'
			}
		}

		if (formData.totalAcidity) {
			const ta = parseFloat(formData.totalAcidity)
			if (isNaN(ta) || ta < 0) {
				newErrors.totalAcidity = 'TA must be a non-negative number'
			}
		}

		if (formData.residualSugar) {
			const rs = parseFloat(formData.residualSugar)
			if (isNaN(rs) || rs < 0) {
				newErrors.residualSugar = 'RS must be a non-negative number'
			}
		}

		if (formData.freeSo2) {
			const fso2 = parseInt(formData.freeSo2, 10)
			if (isNaN(fso2) || fso2 < 0 || !Number.isInteger(parseFloat(formData.freeSo2))) {
				newErrors.freeSo2 = 'FSO2 must be a non-negative integer'
			}
		}

		if (formData.totalSo2) {
			const tso2 = parseInt(formData.totalSo2, 10)
			if (isNaN(tso2) || tso2 < 0 || !Number.isInteger(parseFloat(formData.totalSo2))) {
				newErrors.totalSo2 = 'Total SO2 must be a non-negative integer'
			}
		}

		if (formData.technicalAnalysisDate) {
			const dateRegex = /^\d{4}-\d{2}-\d{2}$/
			if (!dateRegex.test(formData.technicalAnalysisDate)) {
				newErrors.technicalAnalysisDate = 'Date must be in YYYY-MM-DD format'
			} else {
				const date = new Date(formData.technicalAnalysisDate)
				if (isNaN(date.getTime())) {
					newErrors.technicalAnalysisDate = 'Invalid date'
				}
			}
		}

		// Validate compositions
		const totalPercentage = formData.compositions.reduce((sum, comp) => {
			return sum + (parseFloat(comp.percentage) || 0)
		}, 0)

		if (Math.abs(totalPercentage - 100) > 0.01) {
			newErrors.compositions = 'Total percentage must equal 100%'
		}

		formData.compositions.forEach((comp, index) => {
			if (!comp.varietyId) {
				newErrors[`composition_${index}_variety`] = 'Variety is required'
			}
			if (!comp.percentage || parseFloat(comp.percentage) <= 0) {
				newErrors[`composition_${index}_percentage`] = 'Percentage must be positive'
			}
		})

		const varietyCounts = formData.compositions.reduce<Record<string, number>>((counts, comp) => {
			if (!comp.varietyId) {
				return counts
			}

			counts[comp.varietyId] = (counts[comp.varietyId] || 0) + 1
			return counts
		}, {})

		formData.compositions.forEach((comp, index) => {
			if (comp.varietyId && varietyCounts[comp.varietyId] > 1) {
				newErrors[`composition_${index}_variety`] = 'Each variety can only be selected once'
			}
		})

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!validate()) {
			return
		}

		const payloadCompositions = formData.compositions
			.filter((comp) => comp.varietyId && comp.percentage)
			.map((comp) => ({
				varietyId: parseInt(comp.varietyId, 10),
				percentage: parseFloat(comp.percentage),
			}))

		const payload = {
			name: formData.code,
			supplierId: parseInt(formData.supplierId, 10),
			regionId: parseInt(formData.regionId, 10),
			subRegionId: formData.subRegionId ? parseInt(formData.subRegionId, 10) : null,
			locationRegionId: formData.locationRegionId ? parseInt(formData.locationRegionId, 10) : null,
			locationSubRegionId: formData.locationSubRegionId ? parseInt(formData.locationSubRegionId, 10) : null,
			primaryVarietyId: parseInt(formData.primaryVarietyId, 10),
			vintage: parseInt(formData.vintage, 10),
			priceCurrency: formData.priceCurrency,
			pricePerLiter: parseFloat(formData.pricePerLiter),
			priceMargin: formData.priceMargin ? parseFloat(formData.priceMargin) : null,
			priceMarginUnit: formData.priceMarginUnit,
			totalVolumeLiters: formData.totalVolumeLiters ? parseFloat(formData.totalVolumeLiters) : null,
			compositions: payloadCompositions,
			// Technical Specifications
			alcoholPercentage: formData.alcoholPercentage ? parseFloat(formData.alcoholPercentage) : null,
			phLevel: formData.phLevel ? parseFloat(formData.phLevel) : null,
			volatileAcidity: formData.volatileAcidity ? parseFloat(formData.volatileAcidity) : null,
			totalAcidity: formData.totalAcidity ? parseFloat(formData.totalAcidity) : null,
			residualSugar: formData.residualSugar ? parseFloat(formData.residualSugar) : null,
			freeSo2: formData.freeSo2 ? parseInt(formData.freeSo2, 10) : null,
			totalSo2: formData.totalSo2 ? parseInt(formData.totalSo2, 10) : null,
			technicalAnalysisDate: formData.technicalAnalysisDate || null,
			// Compliance
			suitableVegetarians: formData.suitableVegetarians,
			suitableVegans: formData.suitableVegans,
			exportCertified: formData.exportCertified,
			sprayProgrammeFollowed: formData.sprayProgrammeFollowed,
			oakTreatment: formData.oakTreatment,
			proteinStable: formData.proteinStable,
			wineEnriched: formData.wineEnriched,
			calibratedFlowMeter: formData.calibratedFlowMeter,
			filtered: formData.filtered,
			wineSweetened: formData.wineSweetened,
			coldStable: formData.coldStable,
			mlfComplete: formData.mlfComplete,
			oakType: formData.oakType || null,
			oakTreatmentNote: formData.oakTreatmentNote || null,
			filterType: formData.filterType || null,
			filterNote: formData.filterNote || null,
			// Export Eligibility
			exportToEu: formData.exportToEu,
			exportToUk: formData.exportToUk,
			exportToChina: formData.exportToChina,
			exportToJapan: formData.exportToJapan,
			exportToCanada: formData.exportToCanada,
			exportToUsa: formData.exportToUsa,
			// NZ Eligibility
			winerySwinzCertified: formData.winerySwinzCertified,
			vineyardSwinzCertified: formData.vineyardSwinzCertified,
			nzComplianceStatement: formData.nzComplianceStatement,
			// AU Eligibility
			auComplianceStatement: formData.auComplianceStatement,
		}

		setMutationError(null) // Clear previous errors

		try {
			await saveBulkWineMutation.mutateAsync({
				id: isEditMode ? (id as string) : null,
				payload,
			})

			// Only navigate on success
			navigate('/inventory/bulk-wine')
		} catch (error) {
			console.error('Error saving bulk wine:', error)
			setMutationError(error as Error)

			// Scroll to top so user sees error
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}
	}

	const handleCancel = () => {
		navigate('/inventory/bulk-wine')
	}

	return (
		<EntityEditLayout
			backTo="/inventory/bulk-wine"
			backLabel="Back to Bulk Wine Inventory"
			title={isEditMode ? formData.code || 'Bulk Wine' : 'New Bulk Wine Specifications'}
			subtitle={isEditMode ? 'Edit wine listing' : 'Create a new wine listing'}
			titleClassName="font-heading text-[30px] font-bold text-[#101828] leading-[38px]"
		>
			{/* Show mutation error as fixed toast at top */}
			{mutationError && (
				<div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
					<ErrorBanner
						error={mutationError}
						title="Error Saving Bulk Wine"
						onRetry={handleSubmit}
						onDismiss={() => setMutationError(null)}
					/>
				</div>
			)}

			<form onSubmit={handleSubmit}>
				<Card className="mb-6">
					<h2 className="text-xl font-semibold mb-4">Basic Information</h2>
					<p className="mb-4 text-base font-normal leading-5 text-[#667085]">Core details about the wine listing</p>

					<div className="grid grid-cols-2 gap-4">
						<Input
							label="Wine Code"
							name="code"
							value={formData.code}
							onChange={handleChange}
							error={errors.code}
							placeholder="Auto-generated (e.g., AB24PN)"
							hint="Auto-filled from vintage + variety. Edit to set your own."
							rightAdornment={
								<button
									type="button"
									onClick={handleRegenerateCode}
									disabled={!canGenerateCode || regeneratingCode}
									title="Regenerate code"
									aria-label="Regenerate code"
									className="p-1 text-neutral-500 hover:text-primary-600 disabled:text-neutral-300 disabled:cursor-not-allowed"
								>
									<RefreshCw size={16} className={regeneratingCode ? 'animate-spin' : ''} />
								</button>
							}
						/>

						{/* col-start-1 keeps Supplier off the Wine Code row, whose hint makes that
						    cell taller. Country does the same so Country/Region stay paired. */}
						<div className="col-start-1">
							{isSupplierAdmin ? (
								<Input label="Supplier" value={supplierAdminSupplierName} readOnly placeholder="Loading supplier..." />
							) : (
								<>
									<label className="block text-sm font-medium text-neutral-700 mb-1">Supplier</label>
									<select
										name="supplierId"
										value={formData.supplierId}
										onChange={handleChange}
										className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
									>
										<option value="">Select supplier...</option>
										{suppliers.map((supplier) => (
											<option key={supplier.id} value={supplier.id}>
												{supplier.name}
											</option>
										))}
									</select>
								</>
							)}
							{errors.supplierId && <p className="mt-1 text-sm text-red-600">{errors.supplierId}</p>}
						</div>

						<div className="col-start-1">
							<label className="block text-sm font-medium text-neutral-700 mb-1">Country</label>
							<select
								name="countryId"
								value={formData.countryId}
								onChange={(e) => {
									const countryId = e.target.value
									setSelectedCountryId(countryId)
									// The selected region/sub-region belong to the previous country.
									setFormData((prev) => ({ ...prev, countryId, regionId: '', subRegionId: '' }))
									if (errors.countryId) {
										setErrors((prev) => ({ ...prev, countryId: undefined }))
									}
								}}
								className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
							>
								<option value="">Select country...</option>
								{countries.map((country) => (
									<option key={country.id} value={String(country.id)}>
										{country.name}
									</option>
								))}
							</select>
							{errors.countryId && <p className="mt-1 text-sm text-red-600">{errors.countryId}</p>}
						</div>

						<div>
							<label className="block text-sm font-medium text-neutral-700 mb-1">Region</label>
							{/* Regions and their sub-regions share one dropdown: sub-regions are grouped
                  under their parent, and picking one sets both IDs. Regions without
                  sub-regions (e.g. South Eastern Australia) appear as plain options. */}
							<select
								name="regionId"
								value={regionSelectValue}
								onChange={handleRegionSelectChange}
								className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
								disabled={!selectedCountryId}
							>
								<option value="">Select region...</option>
								{regionOptionGroups.map(({ region, subRegions }) =>
									subRegions.length > 0 ? (
										<optgroup key={region.id} label={region.name}>
											<option value={`r:${region.id}`}>{region.name}</option>
											{subRegions.map((subRegion) => (
												<option key={subRegion.id} value={`s:${subRegion.id}`}>
													{subRegion.name}
												</option>
											))}
										</optgroup>
									) : (
										<option key={region.id} value={`r:${region.id}`}>
											{region.name}
										</option>
									),
								)}
							</select>
							{errors.regionId && <p className="mt-1 text-sm text-red-600">{errors.regionId}</p>}
						</div>

						<div>
							<label className="block text-sm font-medium text-neutral-700 mb-1">Variety *</label>
							<select
								name="primaryVarietyId"
								value={formData.primaryVarietyId}
								onChange={(e) => {
									const primaryVarietyId = e.target.value
									setFormData((prev) => ({
										...prev,
										primaryVarietyId,
										compositions:
											prev.compositions.length === 1 && !prev.compositions[0].varietyId
												? [{ ...prev.compositions[0], varietyId: primaryVarietyId }]
												: prev.compositions,
									}))
									if (errors.primaryVarietyId) {
										setErrors((prev) => ({ ...prev, primaryVarietyId: undefined }))
									}
								}}
								className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
							>
								<option value="">Select variety...</option>
								{varieties.map((variety) => (
									<option key={variety.id} value={String(variety.id)}>
										{variety.name}
									</option>
								))}
							</select>
							{errors.primaryVarietyId && <p className="mt-1 text-sm text-red-600">{errors.primaryVarietyId}</p>}
						</div>

						<Input
							label="Vintage *"
							name="vintage"
							type="number"
							step="1"
							value={formData.vintage}
							onChange={handleChange}
							error={errors.vintage}
							placeholder="2024"
						/>

						<div>
							<label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
							{/* Same options as Region, but over every region rather than the selected
                  country's, and optional: it records where the wine is held so buyers can
                  estimate freight, which may be nowhere near where the fruit was grown. */}
							<select
								name="locationRegionId"
								value={locationSelectValue}
								onChange={handleLocationSelectChange}
								className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
							>
								<option value="">Select location...</option>
								{locationOptionGroups.map(({ region, subRegions }) =>
									subRegions.length > 0 ? (
										<optgroup key={region.id} label={region.name}>
											<option value={`r:${region.id}`}>{region.name}</option>
											{subRegions.map((subRegion) => (
												<option key={subRegion.id} value={`s:${subRegion.id}`}>
													{subRegion.name}
												</option>
											))}
										</optgroup>
									) : (
										<option key={region.id} value={`r:${region.id}`}>
											{region.name}
										</option>
									),
								)}
							</select>
							{/* Region above is the wine's origin, so spell out that this is a different
                  thing — otherwise the two read as duplicates. */}
							<FieldHint>Where the wine is currently stored, not where it was grown.</FieldHint>
						</div>

						{/* col-start-1 drops this onto its own row rather than sitting beside Location,
                whose description makes that cell taller. */}
						<div className="col-start-1">
							<Input
								label="Total Volume (Litres)"
								name="totalVolumeLiters"
								type="number"
								step="0.01"
								value={formData.totalVolumeLiters}
								onChange={handleChange}
								error={errors.totalVolumeLiters}
								placeholder="0.00"
							/>
						</div>
					</div>
				</Card>

				<Card className="mb-6">
					<div className="mb-4">
						<h2 className="text-xl font-semibold">Composition</h2>
					</div>

					{errors.compositions && <p className="mb-4 text-sm text-red-600">{errors.compositions}</p>}

					<div className="space-y-3">
						{formData.compositions.map((comp, index) => (
							<div key={index} className="flex gap-3 items-start">
								<div className="flex-1">
									<select
										value={comp.varietyId}
										onChange={(e) => handleCompositionChange(index, 'varietyId', e.target.value)}
										className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
									>
										<option value="">Select variety...</option>
										{getAvailableCompositionVarieties(index).map((variety) => (
											<option key={variety.id} value={String(variety.id)}>
												{variety.name}
											</option>
										))}
									</select>
									{errors[`composition_${index}_variety`] && (
										<p className="mt-1 text-sm text-red-600">{errors[`composition_${index}_variety`]}</p>
									)}
								</div>

								<div className="w-32">
									<div className="relative">
										<input
											type="number"
											step="0.01"
											value={comp.percentage}
											onChange={(e) => handleCompositionChange(index, 'percentage', e.target.value)}
											className="w-full rounded-md border border-neutral-300 px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-primary-500"
											placeholder="100"
										/>
										<span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-neutral-500">
											%
										</span>
									</div>
									{errors[`composition_${index}_percentage`] && (
										<p className="mt-1 text-sm text-red-600">{errors[`composition_${index}_percentage`]}</p>
									)}
								</div>

								{formData.compositions.length > 1 && (
									<button
										type="button"
										onClick={() => removeComposition(index)}
										aria-label="Remove variety"
										className="pt-2 hover:opacity-70"
									>
										<X size={20} color="#667085" strokeWidth={1.66667} aria-hidden="true" />
									</button>
								)}
							</div>
						))}
					</div>

					<div className="mt-4 flex justify-start">
						<Button type="button" variant="cream" size="md" onClick={addComposition} disabled={!canAddComposition()}>
							Add Variety
						</Button>
					</div>
				</Card>

				<Card className="mb-6">
					<h2 className="text-xl font-semibold mb-4">Pricing</h2>
					<p className="mb-4 text-base font-normal leading-5 text-[#667085]">Set your asking price</p>

					<div className="mb-4 w-full md:w-1/3">
						<label className="mb-1 block text-sm font-medium text-neutral-700">Price Currency</label>
						<select
							name="priceCurrency"
							value={formData.priceCurrency}
							onChange={handleChange}
							className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
						>
							<option value="NZD">NZD</option>
							<option value="AUD">AUD</option>
						</select>
					</div>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<div>
							<label className="mb-1 block text-sm font-medium text-neutral-700">Price per Liter</label>
							<div className="relative">
								<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">
									$
								</span>
								<input
									name="pricePerLiter"
									type="number"
									step="0.01"
									value={formData.pricePerLiter}
									onChange={handleChange}
									className="w-full rounded-md border border-neutral-300 py-2 pl-8 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
									placeholder="0.00"
								/>
							</div>
							{errors.pricePerLiter && <p className="mt-1 text-sm text-red-600">{errors.pricePerLiter}</p>}
						</div>

						{isSuperadmin && (
							<div className="grid grid-cols-[minmax(0,1fr)_104px] gap-3">
								<Input
									label="Margin"
									name="priceMargin"
									type="number"
									step="0.01"
									value={formData.priceMargin}
									onChange={handleChange}
									error={errors.priceMargin}
									placeholder="0.00"
								/>

								<div>
									<label className="mb-1 block text-sm font-medium text-neutral-700">Margin Unit</label>
									<select
										name="priceMarginUnit"
										value={formData.priceMarginUnit}
										onChange={handleChange}
										className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
									>
										<option value="%">%</option>
										<option value="$">$</option>
									</select>
								</div>
							</div>
						)}
					</div>
				</Card>

				{/* Technical Specifications Section */}
				<Card className="mb-6">
					<h2 className="text-xl font-semibold mb-4">Technical Specifications</h2>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{/* Left Column */}
						<Input
							label="Alcohol (%)"
							name="alcoholPercentage"
							type="number"
							step="0.01"
							min="0"
							max="100"
							value={formData.alcoholPercentage}
							onChange={handleChange}
							placeholder="0"
							error={errors.alcoholPercentage}
						/>

						<Input
							label="Total Acidity: TA (g/L)"
							name="totalAcidity"
							type="number"
							step="0.01"
							min="0"
							value={formData.totalAcidity}
							onChange={handleChange}
							placeholder="0"
							error={errors.totalAcidity}
						/>

						<Input
							label="pH"
							name="phLevel"
							type="number"
							step="0.01"
							min="0"
							max="14"
							value={formData.phLevel}
							onChange={handleChange}
							placeholder="0"
							error={errors.phLevel}
						/>

						<Input
							label="Residual Sugar: RS (g/L)"
							name="residualSugar"
							type="number"
							step="0.01"
							min="0"
							value={formData.residualSugar}
							onChange={handleChange}
							placeholder="0"
							error={errors.residualSugar}
						/>

						<Input
							label="Volatile Acidity: VA (g/L)"
							name="volatileAcidity"
							type="number"
							step="0.01"
							min="0"
							value={formData.volatileAcidity}
							onChange={handleChange}
							placeholder="0"
							error={errors.volatileAcidity}
						/>

						<Input
							label="Free Sulfur Dioxide: FSO2 (ppm)"
							name="freeSo2"
							type="number"
							min="0"
							value={formData.freeSo2}
							onChange={handleChange}
							placeholder="0"
							error={errors.freeSo2}
						/>

						<Input
							label="Total Sulfur Dioxide: TSO2 (ppm)"
							name="totalSo2"
							type="number"
							min="0"
							value={formData.totalSo2}
							onChange={handleChange}
							placeholder="0"
							error={errors.totalSo2}
						/>

						<Input
							label="Date"
							name="technicalAnalysisDate"
							type="date"
							value={formData.technicalAnalysisDate}
							onChange={handleChange}
							error={errors.technicalAnalysisDate}
						/>
					</div>
				</Card>

				{/* Compliance, Export Eligibility & NZ Eligibility - Combined Section */}
				<Card className="mb-6">
					{/* Compliance */}
					<h2 className="text-xl font-semibold mb-4">Compliance</h2>

					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
						<Checkbox
							name="suitableVegetarians"
							label="Suitable for Vegetarians"
							checked={formData.suitableVegetarians}
							onChange={handleCheckboxChange('suitableVegetarians')}
						/>
						<Checkbox
							name="proteinStable"
							label="Protein Stable"
							checked={formData.proteinStable}
							onChange={handleCheckboxChange('proteinStable')}
						/>
						<Checkbox
							name="filtered"
							label="Filtered"
							checked={formData.filtered}
							onChange={handleCheckboxChange('filtered')}
						/>

						<Checkbox
							name="suitableVegans"
							label="Suitable for Vegans"
							checked={formData.suitableVegans}
							onChange={handleCheckboxChange('suitableVegans')}
						/>
						<Checkbox
							name="wineEnriched"
							label="Wine enriched"
							checked={formData.wineEnriched}
							onChange={handleCheckboxChange('wineEnriched')}
						/>
						<Checkbox
							name="wineSweetened"
							label="Wine Sweetened"
							checked={formData.wineSweetened}
							onChange={handleCheckboxChange('wineSweetened')}
						/>

						<Checkbox
							name="exportCertified"
							label="Export Certified"
							checked={formData.exportCertified}
							onChange={handleCheckboxChange('exportCertified')}
						/>
						<Checkbox
							name="sprayProgrammeFollowed"
							label="Spray Programme Followed"
							checked={formData.sprayProgrammeFollowed}
							onChange={handleCheckboxChange('sprayProgrammeFollowed')}
						/>
						<Checkbox
							name="coldStable"
							label="Cold Stable"
							checked={formData.coldStable}
							onChange={handleCheckboxChange('coldStable')}
						/>

						<Checkbox
							name="oakTreatment"
							label="Oak Treatment"
							checked={formData.oakTreatment}
							onChange={handleCheckboxChange('oakTreatment')}
						/>
						<Checkbox
							name="calibratedFlowMeter"
							label="Calibrated Flow Meter Present"
							checked={formData.calibratedFlowMeter}
							onChange={handleCheckboxChange('calibratedFlowMeter')}
						/>
						<Checkbox
							name="mlfComplete"
							label="MLF Complete"
							checked={formData.mlfComplete}
							onChange={handleCheckboxChange('mlfComplete')}
						/>
					</div>

					{/* Conditional Oak/Filter Fields */}
					{(formData.oakTreatment || formData.filtered) && (
						<div className="bg-neutral-100 rounded-md p-4 space-y-4 mb-6">
							{formData.oakTreatment && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Select
										label="Oak Type:"
										options={[
											{ value: 'Oak Barrels', label: 'Oak Barrels' },
											{ value: 'Oak Chips', label: 'Oak Chips' },
											{ value: 'Oak Staves', label: 'Oak Staves' },
											{ value: 'Oak Beans', label: 'Oak Beans' },
											{ value: 'No Oak Treatment', label: 'No Oak Treatment' },
										]}
										value={formData.oakType}
										onChange={handleSelectChange('oakType')}
									/>
									<Textarea
										label="Note:"
										name="oakTreatmentNote"
										value={formData.oakTreatmentNote}
										onChange={handleChange}
										rows={2}
										placeholder=""
									/>
								</div>
							)}

							{formData.filtered && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Select
										label="Filter Type:"
										options={[
											{ value: 'Filter Type', label: 'Filter Type' },
											{ value: 'Pad Filter', label: 'Pad Filter' },
											{ value: 'Membrane Filter', label: 'Membrane Filter' },
											{ value: 'Crossflow Filter', label: 'Crossflow Filter' },
											{ value: 'Unfiltered', label: 'Unfiltered' },
										]}
										value={formData.filterType}
										onChange={handleSelectChange('filterType')}
									/>
									<Textarea
										label="Note:"
										name="filterNote"
										value={formData.filterNote}
										onChange={handleChange}
										rows={2}
										placeholder=""
									/>
								</div>
							)}
						</div>
					)}

					{/* Export Eligibility */}
					<h3 className="text-lg font-semibold mb-4 mt-6">Export Eligibility</h3>

					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
						<Checkbox
							name="exportToEu"
							label="EU"
							checked={formData.exportToEu}
							onChange={handleCheckboxChange('exportToEu')}
						/>
						<Checkbox
							name="exportToUk"
							label="UK"
							checked={formData.exportToUk}
							onChange={handleCheckboxChange('exportToUk')}
						/>
						<Checkbox
							name="exportToChina"
							label="China"
							checked={formData.exportToChina}
							onChange={handleCheckboxChange('exportToChina')}
						/>
						<Checkbox
							name="exportToJapan"
							label="Japan"
							checked={formData.exportToJapan}
							onChange={handleCheckboxChange('exportToJapan')}
						/>
						<Checkbox
							name="exportToCanada"
							label="Canada"
							checked={formData.exportToCanada}
							onChange={handleCheckboxChange('exportToCanada')}
						/>
						<Checkbox
							name="exportToUsa"
							label="USA"
							checked={formData.exportToUsa}
							onChange={handleCheckboxChange('exportToUsa')}
						/>
					</div>

					{/* Region eligibility - Conditional visibility based on selected country */}
					{(() => {
						const selectedCountry = countries.find((c) => String(c.id) === formData.countryId)
						const isNewZealand = selectedCountry?.code === 'NZ'
						const isAustralia = selectedCountry?.code === 'AU'
						if (isNewZealand) {
							return (
								<>
									<h3 className="text-lg font-semibold mb-4 mt-6">Eligibility (New Zealand only)</h3>

									<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
										<Checkbox
											name="winerySwinzCertified"
											label="Winery SWNZ Certified"
											checked={formData.winerySwinzCertified}
											onChange={handleCheckboxChange('winerySwinzCertified')}
										/>
										<Checkbox
											name="vineyardSwinzCertified"
											label="Vineyard SWNZ Certified"
											checked={formData.vineyardSwinzCertified}
											onChange={handleCheckboxChange('vineyardSwinzCertified')}
										/>
										<Checkbox
											name="nzComplianceStatement"
											label="New Zealand Compliance Statement"
											checked={formData.nzComplianceStatement}
											onChange={handleCheckboxChange('nzComplianceStatement')}
										/>
									</div>
								</>
							)
						}
						if (isAustralia) {
							return (
								<>
									<h3 className="text-lg font-semibold mb-4 mt-6">Eligibility (Australia only)</h3>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<Checkbox
											name="auComplianceStatement"
											label="Australian Compliance Statement"
											checked={formData.auComplianceStatement}
											onChange={handleCheckboxChange('auComplianceStatement')}
										/>
									</div>
								</>
							)
						}
						return null
					})()}
				</Card>

				<div className="flex justify-end gap-3 mt-8">
					<Button type="button" variant="ghost" onClick={handleCancel} disabled={saveBulkWineMutation.isPending}>
						Cancel
					</Button>
					<Button type="submit" variant="primary" disabled={saveBulkWineMutation.isPending}>
						{saveBulkWineMutation.isPending ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Bulk Wine'}
					</Button>
				</div>
			</form>
		</EntityEditLayout>
	)
}

export default BulkWineEditPage
